import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authFetch } from '../../utils/api';
import {
  Typography,
  App as AntApp,
  Alert,
  Table,
  Button,
  Modal,
  Space,
  Popconfirm,
  Divider,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { DangerousActionModal } from '../../shared/form';
import { TierFormModal, TIER_SNAPSHOT_POLICY_NOTICE, type TierRecordForForm } from './TierFormModal';
import { ScheduleFormModal, type ScheduleRecordForForm } from './ScheduleFormModal';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { getBrandLabel, type Brand } from '../../utils/tokenManager';
import { useViewScope } from '../../utils/viewScope';
import { PolicyUpdateNotice } from './PolicyUpdateNotice';
import { MAX_AUTO_POLICY_CHECKS, retainTierPolicyChanges, tierPolicyChangeKey, parseTierMutationResult, TierMutationSession, tierExpectedRevisions, tierMutationError } from './policyUpdates';
import type { TierSavedChange } from './policyUpdates';

const { Title } = Typography;

/* ───────── Types ───────── */

interface TierSchedule {
  id: number;
  tierId: number;
  dayOfWeek: number | null;
  startTime: string;
  endTime: string;
  downloadPercent: number;
  uploadPercent: number;
  priority: number;
  createdAt: string;
}

interface TierRecord extends TierRecordForForm {
  brand: Brand;
  userCount: number;
  schedules: TierSchedule[];
  createdAt: string;
}

/* ───────── Helpers ───────── */

function formatTrafficMb(value: number | null | undefined): string {
  if (value == null || value === 0) return '無限制';
  if (value >= 1024) {
    const gb = value / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${value} MB`;
}

function formatMbps(value: number | null | undefined): string {
  if (value == null || value === 0) return '無限制';
  return `${value} Mbps`;
}

function formatMinutes(value: number | null | undefined): string {
  if (value == null || value === 0) return '無限制';
  return `${value} 分鐘`;
}

function formatDay(dayOfWeek: number | null | undefined): string {
  const map: Record<number, string> = {
    0: '星期日',
    1: '星期一',
    2: '星期二',
    3: '星期三',
    4: '星期四',
    5: '星期五',
    6: '星期六',
  };
  return dayOfWeek == null ? '每天' : map[dayOfWeek] ?? '-';
}

/* ───────── Component ───────── */

/**
 * 等級管理頁面 — Pattern A++ 範本
 *
 * 主表單拆成獨立元件 TierFormModal（含 FormSection 分區），
 * 排程表單拆成 ScheduleFormModal，本檔只負責列表 + 排程清單管理。
 *
 * 為什麼不用 Pattern B 全頁 + FormArray：
 * - schedules 是後端獨立資源（POST /tiers/:id/schedules），無 atomic upsert API
 * - 客戶端 diff 拆分為多個 POST/PUT/DELETE 不 atomic、出錯難回滾
 * - 真正的 FormArray 範本會留給 nodes 的 protocols 陣列（單一 PUT 提交）
 */
export const TierList: React.FC = () => {
  const { message } = AntApp.useApp();
  // 檢視範圍（去品牌化 M1）。單品牌檢視 = brandForRequest；聚合 = null。
  const { scope: viewScope, brandForRequest, isAllScope } = useViewScope();
  const viewBrandLabel = isAllScope ? '全部品牌' : getBrandLabel(viewScope);
  const [data, setData] = useState<TierRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [changes, setChanges] = useState<TierSavedChange[]>([]);
  const reads = useRef<AbortController | null>(null);
  const scheduleReads = useRef<AbortController | null>(null);
  const writes = useRef<AbortController | null>(null);
  const deleteMutation = useRef(new TierMutationSession());
  const scheduleDeleteMutation = useRef(new TierMutationSession());
  const [deletingSchedule, setDeletingSchedule] = useState<number | null>(null);
  const scheduleDeleteBusy = useRef(false);

  // 主 tier form
  const [tierFormOpen, setTierFormOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<TierRecord | null>(null);

  // 刪除 tier
  const [deleteTarget, setDeleteTarget] = useState<TierRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 排程清單 modal
  const [scheduleListOpen, setScheduleListOpen] = useState(false);
  const [scheduleTier, setScheduleTier] = useState<TierRecord | null>(null);
  const [schedules, setSchedules] = useState<TierSchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);

  // 排程編輯 form
  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleRecordForForm | null>(null);

  /* ── Fetch tiers ── */
  const fetchData = useCallback(async () => {
    reads.current?.abort();
    const controller = new AbortController(); reads.current = controller;
    setLoading(true);
    try {
      const path = `/api/admin/tiers${brandForRequest ? `?brand=${encodeURIComponent(brandForRequest)}` : ''}`;
      const res = await authFetch(path, { signal: controller.signal });
      const result = await res.json();
      if (controller.signal.aborted) return;
      if (!res.ok || result.code !== 0) throw new Error(result.msg || '等級資料讀取失敗');
      const list: TierRecord[] = Array.isArray(result.data) ? result.data : [];
      if (brandForRequest && list.some(row => row.brand !== brandForRequest)) throw new Error('回應品牌與檢視範圍不符，請重新讀取。');
      setData(list); setReadError(null);
      setScheduleTier(current => current ? list.find(row => row.id === current.id && row.brand === current.brand) ?? current : null);
    } catch (cause) {
      if (!controller.signal.aborted) setReadError(cause instanceof Error ? cause.message : '等級資料讀取失敗');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    // brandForRequest 進 deps：檢視範圍變更（X-Brand 隨之改）時重拉等級清單。
  }, [brandForRequest]);

  useEffect(() => {
    void fetchData();
    return () => reads.current?.abort();
  }, [fetchData]);

  useEffect(() => {
    setChanges([]); setTierFormOpen(false); setEditingTier(null);
    setScheduleListOpen(false); setScheduleFormOpen(false); setScheduleTier(null);
    setDeleteTarget(null); setSchedules([]); setData([]);
    deleteMutation.current.clear(); scheduleDeleteMutation.current.clear();
    return () => { writes.current?.abort(); scheduleReads.current?.abort(); };
  }, [brandForRequest]);

  const recordSaved = (change: TierSavedChange) => {
    setChanges(current => retainTierPolicyChanges(current, change));
    void fetchData();
  };

  // 新增等級：等級屬品牌隔離資源，聚合檢視下無明確目標品牌 → 先擋，提示切單一品牌。
  // （M4 TargetBrandField 尚未接進此表單，先用檢視 gate 守住品牌隔離紅線。）
  const openCreateTier = () => {
    if (isAllScope) {
      message.warning('請先在右上角「檢視範圍」切換到單一品牌，再新增等級。');
      return;
    }
    setEditingTier(null);
    setTierFormOpen(true);
  };

  /* ── Delete tier ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if ((deleteTarget.activePaidReferenceCount ?? 0) > 0) throw new Error('此等級仍被付費權益、訂閱或訂單引用，不能直接刪除。');
      // M4：刪除既有 row 的寫入，目標品牌 = 該 row 的品牌（列表已帶 brand 欄），
      // 顯式帶 X-Target-Brand，絕不繼承檢視範圍 / activeBrand。
      const path = `/api/admin/tiers/${deleteTarget.id}`;
      const controller = new AbortController(); writes.current = controller;
      const res = await authFetch(path, { ...deleteMutation.current.options(path, 'DELETE', deleteTarget.brand, tierExpectedRevisions(deleteTarget)), signal: controller.signal });
      const result = await res.json();
      if (controller.signal.aborted) return;
      if (res.ok && result.code === 0) {
        message.success('等級刪除請求已保存，請核對套用狀態');
        recordSaved({ tierId: deleteTarget.id, brand: deleteTarget.brand, name: deleteTarget.name, update: parseTierMutationResult(result.data).policyUpdate });
        deleteMutation.current.clear();
        setDeleteTarget(null);
      } else {
        message.error(tierMutationError(result));
      }
    } catch (cause) {
      if (!writes.current?.signal.aborted) message.error(cause instanceof TypeError ? '伺服器尚未確認結果，已保留原刪除請求，請重試。' : cause instanceof Error ? cause.message : '刪除結果未確認，請重試原請求');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Schedules list ── */
  const openScheduleList = async (record: TierRecord) => {
    setScheduleTier(record);
    setScheduleListOpen(true);
    await fetchSchedules(record.id, record.brand);
  };

  const fetchSchedules = async (tierId: number, brand: string) => {
    scheduleReads.current?.abort();
    const controller = new AbortController(); scheduleReads.current = controller;
    setSchedulesLoading(true);
    try {
      const res = await authFetch(`/api/admin/tiers/${tierId}/schedules?brand=${encodeURIComponent(brand)}`, { signal: controller.signal });
      const result = await res.json();
      if (controller.signal.aborted) return;
      if (result.code === 0) {
        setSchedules(Array.isArray(result.data) ? result.data : []);
      }
    } catch {
      // ignore
    } finally {
      if (!controller.signal.aborted) setSchedulesLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!scheduleTier) return;
    if (scheduleDeleteBusy.current) return;
    scheduleDeleteBusy.current = true; setDeletingSchedule(scheduleId);
    try {
      // M4：排程隸屬某個 tier row，刪除為既有 row 寫入，目標品牌 = 該 tier 的品牌
      //（scheduleTier.brand，列表已知），顯式帶 X-Target-Brand，絕不繼承檢視範圍。
      const path = `/api/admin/tiers/${scheduleTier.id}/schedules/${scheduleId}`;
      const controller = new AbortController(); writes.current = controller;
      const res = await authFetch(path, { ...scheduleDeleteMutation.current.options(path, 'DELETE', scheduleTier.brand, tierExpectedRevisions(scheduleTier)), signal: controller.signal });
      const result = await res.json();
      if (controller.signal.aborted) return;
      if (res.ok && result.code === 0) {
        message.success('排程刪除請求已保存，請核對套用狀態');
        scheduleDeleteMutation.current.clear();
        recordSaved({ tierId: scheduleTier.id, brand: scheduleTier.brand, name: scheduleTier.name, update: parseTierMutationResult(result.data).policyUpdate });
        fetchSchedules(scheduleTier.id, scheduleTier.brand);
      } else {
        message.error(tierMutationError(result));
      }
    } catch (cause) {
      if (!writes.current?.signal.aborted) message.error(cause instanceof TypeError ? '伺服器尚未確認結果，已保留原刪除請求，請重試。' : cause instanceof Error ? cause.message : '刪除結果未確認，請重試原請求');
    } finally {
      scheduleDeleteBusy.current = false; setDeletingSchedule(null);
    }
  };

  /* ── Schedule table columns ── */
  const scheduleColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '日期',
      dataIndex: 'dayOfWeek',
      render: (dayOfWeek: number | null) => formatDay(dayOfWeek),
    },
    {
      title: '時段',
      render: (_: unknown, r: TierSchedule) =>
        `${String(r.startTime).slice(0, 5)} ~ ${String(r.endTime).slice(0, 5)}`,
    },
    {
      title: '下載',
      dataIndex: 'downloadPercent',
      width: 80,
      render: (value: number) => `${value}%`,
    },
    {
      title: '上傳',
      dataIndex: 'uploadPercent',
      width: 80,
      render: (value: number) => `${value}%`,
    },
    { title: '優先序', dataIndex: 'priority', width: 80 },
    {
      title: '操作',
      width: 120,
      render: (_: unknown, record: TierSchedule) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            aria-label="編輯排程"
            disabled={deletingSchedule !== null || (scheduleTier?.activePaidReferenceCount ?? 0) > 0}
            onClick={() => {
              setEditingSchedule(record);
              setScheduleFormOpen(true);
            }}
          />
          <Popconfirm
            title="確定刪除此排程？"
            onConfirm={() => handleDeleteSchedule(record.id)}
            okText="確定"
            cancelText="取消"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label="刪除排程"
              disabled={deletingSchedule !== null}
              loading={deletingSchedule === record.id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-enter">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }} data-testid="tiers-title">
          等級方案
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          data-testid="tiers-add-btn"
          onClick={openCreateTier}
        >
          新增等級
        </Button>
      </div>

      <BrandScopeNotice kind="tiers" style={{ marginBottom: 12 }} />
      <Alert type="info" showIcon message="目前 Tier 與付費權益" description={TIER_SNAPSHOT_POLICY_NOTICE} style={{ marginBottom: 12 }} />
      {readError && <Alert type="error" showIcon message={readError} action={<Button onClick={() => void fetchData()}>重新讀取</Button>} style={{ marginBottom: 12 }} />}
      {changes.filter(change => !brandForRequest || change.brand === brandForRequest).map((change, index, scopedChanges) => <PolicyUpdateNotice key={tierPolicyChangeKey(change)} change={change} autoPoll={index >= scopedChanges.length - MAX_AUTO_POLICY_CHECKS} />)}

      <Table
        dataSource={data.filter(row => !brandForRequest || row.brand === brandForRequest)}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 100 }}
        scroll={{ x: 1700 }}
        data-testid="tiers-table"
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: '40px 0' }}>
              <p>尚無等級方案，建立等級以管理用戶權限</p>
              <Button type="primary" onClick={openCreateTier}>
                建立第一個等級
              </Button>
            </div>
          ),
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column
          dataIndex="brand"
          title="品牌"
          width={90}
          render={(brand: Brand) => <Tag color="blue">{getBrandLabel(brand)}</Tag>}
        />
        <Table.Column dataIndex="name" title="名稱" width={140} />
        <Table.Column
          dataIndex="description"
          title="描述"
          width={200}
          ellipsis
          render={(v: string | null) => v || '-'}
        />
        <Table.Column
          dataIndex="isFree"
          title="類型"
          width={80}
          render={(isFree: boolean) => {
            // K盾 單品牌檢視用「預設 / 內部」命名；其餘或聚合檢視用中性「免費 / 付費」。
            const kshieldNaming = viewScope === 'kshield';
            return isFree ? (
              <Tag color="green">{kshieldNaming ? '預設' : '免費'}</Tag>
            ) : (
              <Tag color="blue">{kshieldNaming ? '內部' : '付費'}</Tag>
            );
          }}
        />
        <Table.Column
          dataIndex="canAccessVless"
          title="VLESS"
          width={80}
          render={(v: boolean) => (v ? <Tag color="purple">開放</Tag> : <Tag>-</Tag>)}
        />
        {/* W3-G (sprint19) — DENY-ALL badge for tiers with allowedNodeIds=[] */}
        <Table.Column
          dataIndex="allowedNodeIds"
          title="ACL"
          width={120}
          render={(allowed: number[] | null) => {
            if (allowed === null || allowed === undefined) {
              return <Tag color="green">ALLOW-ALL</Tag>;
            }
            if (Array.isArray(allowed) && allowed.length === 0) {
              return (
                <Tag color="red" data-testid="tier-list-deny-all-badge">
                  DENY-ALL
                </Tag>
              );
            }
            return (
              <Tag color="blue">
                白名單 ({Array.isArray(allowed) ? allowed.length : 0})
              </Tag>
            );
          }}
        />
        <Table.Column dataIndex="level" title="等級" width={80} />
        <Table.Column dataIndex="maxDevices" title="最大裝置" width={100} />
        <Table.Column
          dataIndex="downloadLimitMbps"
          title="下載限速"
          width={120}
          render={(v: number | null) => formatMbps(v)}
        />
        <Table.Column
          dataIndex="uploadLimitMbps"
          title="上傳限速"
          width={120}
          render={(v: number | null) => formatMbps(v)}
        />
        <Table.Column
          dataIndex="dailyTrafficMb"
          title="日流量"
          width={120}
          render={(v: number | null) => formatTrafficMb(v)}
        />
        <Table.Column
          dataIndex="sessionDurationMinutes"
          title="單次時長"
          width={120}
          render={(v: number | null) => formatMinutes(v)}
        />
        <Table.Column dataIndex="sortOrder" title="排序" width={80} />
        <Table.Column dataIndex="userCount" title="用戶數" width={80} />
        <Table.Column
          title="操作"
          width={220}
          fixed="right"
          render={(_: unknown, record: TierRecord) => (
            <Space>
              <Button
                size="small"
                data-testid="tiers-edit-btn"
                aria-label="編輯等級"
                onClick={() => {
                  setEditingTier(record);
                  setTierFormOpen(true);
                }}
              >
                編輯
              </Button>
              <Button
                size="small"
                data-testid="tiers-schedule-btn"
                aria-label="排程管理"
                onClick={() => openScheduleList(record)}
              >
                排程
              </Button>
              <Button
                size="small"
                danger
                data-testid="tiers-delete-btn"
                aria-label="刪除等級"
                onClick={() => setDeleteTarget(record)}
              >
                刪除
              </Button>
            </Space>
          )}
        />
      </Table>

      {/* 主 Tier 表單 */}
      <TierFormModal
        open={tierFormOpen && (!editingTier || !brandForRequest || editingTier.brand === brandForRequest)}
        editing={editingTier}
        effectiveBrand={brandForRequest}
        onClose={() => {
          setTierFormOpen(false);
          setEditingTier(null);
        }}
        onSaved={recordSaved}
        onReload={() => void fetchData()}
      />

      {/* 排程清單彈窗 */}
      <Modal
        title={`限速排程 — ${scheduleTier?.name ?? ''}`}
        open={scheduleListOpen && (!brandForRequest || scheduleTier?.brand === brandForRequest)}
        onCancel={() => {
          setScheduleListOpen(false);
          setScheduleTier(null);
          setSchedules([]);
        }}
        footer={null}
        width={720}
        destroyOnHidden
      >
        <Table
          dataSource={schedules}
          loading={schedulesLoading}
          columns={scheduleColumns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 500 }}
          size="small"
          locale={{
            emptyText: schedulesLoading ? undefined : (
              <div style={{ padding: '24px 0', color: 'rgba(0,0,0,0.45)' }}>
                <p>尚未設定任何排程</p>
              </div>
            ),
          }}
        />

        <Divider />
        {(scheduleTier?.activePaidReferenceCount ?? 0) > 0 && <Alert type="info" showIcon message="此等級已被付費權益引用，不支援限速排程；可以刪除既有排程以修正設定。" style={{ marginBottom: 12 }} />}

        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={deletingSchedule !== null || (scheduleTier?.activePaidReferenceCount ?? 0) > 0}
          onClick={() => {
            setEditingSchedule(null);
            setScheduleFormOpen(true);
          }}
        >
          新增限速排程
        </Button>
      </Modal>

      {/* 排程編輯表單 */}
      {scheduleTier && (
        <ScheduleFormModal
          open={scheduleFormOpen}
          sourceTierId={scheduleTier.id}
          sourceBrand={scheduleTier.brand}
          sourceTier={scheduleTier}
          editing={editingSchedule}
          onClose={() => {
            setScheduleFormOpen(false);
            setEditingSchedule(null);
          }}
          onSaved={(change) => {
            recordSaved(change);
            if (scheduleTier) {
              fetchSchedules(scheduleTier.id, scheduleTier.brand);
            }
          }}
        />
      )}

      {/* 刪除等級 — 危險動作 type-to-confirm */}
      <DangerousActionModal
        open={!!deleteTarget}
        title={`刪除等級:${deleteTarget?.name ?? ''}`}
        warning={
          <>
            此動作將軟刪除等級。目前有 <strong>{deleteTarget?.userCount ?? 0}</strong> 位用戶
            使用此等級,刪除後這些用戶會失去等級設定的配額限制。請先批量遷移用戶到其他等級。
          </>
        }
        confirmKeyword={deleteTarget?.name ?? ''}
        confirmHint={
          <>
            為防止誤刪,請輸入等級名稱 <code>{deleteTarget?.name}</code> 確認:
          </>
        }
        confirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmLabel="確認刪除等級"
      />
    </div>
  );
};
